import {
  Connector,
  ConnectSessionsRequest,
  ConnectSessionsResponse,
  IntegrationAccount,
  MergeTag,
} from '@phrasee/phrasee-typings'

import {
  CepIntegration,
  connectInternalApi,
  PayloadCepIntegration,
  TrackingMetricObject,
} from 'common/api'
import type { IntegrationProvider } from 'common/api/integrationsApi'

import { mapBEMergeTag } from './helpers'

export const getAvailableIntegrations = async (): Promise<
  IntegrationProvider[]
> => {
  const result = await connectInternalApi.get<Connector[]>(
    'v1/content-integrations/connectors'
  )

  return result.data.map((integration) => ({
    id: integration.provider,
    name: integration.provider_name,
    category: integration.category,
    logoUrl: integration.resources?.images?.logo_url,
  }))
}

export const getReactHubToken = async ({
  provider,
  accountId,
  accountName,
  userName,
  label,
}: ConnectSessionsRequest): Promise<string> => {
  const result = await connectInternalApi.post<ConnectSessionsResponse>(
    'v1/content-integrations/connect_sessions',
    {
      provider,
      accountId,
      accountName,
      userName,
      label,
    }
  )

  return result.data.token
}

export const updateIntegration = async ({
  id,
  label,
}: Pick<IntegrationAccount, 'id' | 'label'>) => {
  const result = await connectInternalApi.patch<ConnectSessionsResponse>(
    `v1/content-integrations/accounts/${id}`,
    { label }
  )

  return result.data.token
}

export const deleteIntegration = async ({
  id,
}: Pick<IntegrationAccount, 'id'>) => {
  const result = await connectInternalApi.delete<IntegrationAccount>(
    `v1/content-integrations/accounts/${id}`
  )

  return result.data
}

export type MergeTagListItem = {
  id: string
  name: string
  description: string
  integration: string
  category: string
  code: string
  created: string
  lastModified: string
  exampleValues?: string[]
}

export const getMergeTags = async ({
  accountId,
}: {
  accountId: string
}): Promise<MergeTagListItem[]> => {
  const result = await connectInternalApi.get<MergeTag[]>(
    `v1/core/monorail/accounts/${accountId}/merge-tags`
  )

  return result.data.map(mapBEMergeTag)
}

export const getSystemMergeTags = async (): Promise<MergeTagListItem[]> => {
  const result = await connectInternalApi.get<MergeTag[]>(
    `v1/core/monorail/merge-tag-templates`
  )

  return result.data.map(mapBEMergeTag)
}

export const createMergeTag = async ({
  accountId,
  name,
  description,
  category,
  code,
  example_values,
  integration,
}: Omit<MergeTag, '_id' | 'last_updated' | 'created'> & {
  accountId: string
}) => {
  await connectInternalApi.post(
    `v1/core/monorail/accounts/${accountId}/merge-tags`,
    {
      name,
      description,
      category,
      code,
      example_values,
      integration,
    }
  )
}

export const deleteMergeTag = async ({
  tagId,
  accountId,
}: {
  tagId: string
  accountId: string
}) => {
  await connectInternalApi.delete(
    `v1/core/monorail/accounts/${accountId}/merge-tags/${tagId}`
  )
}

export const updateMergeTag = async ({
  accountId,
  name,
  description,
  code,
  example_values,
  tagId,
}: Omit<MergeTag, '_id' | 'last_updated' | 'category' | 'integration'> & {
  accountId: string
  tagId: string
}) => {
  await connectInternalApi.patch(
    `v1/core/monorail/accounts/${accountId}/merge-tags/${tagId}`,
    {
      name,
      description,
      code,
      example_values,
    }
  )
}

export type LabelValueObject = {
  label: string
  value: string
}

export type CepTemplate = {
  _id: string
  cep_name: string
  distribution_channel: DistributionChannel[]
}

export type TestingMethodology = {
  [key: string]: any
}

export type DistributionChannel = {
  name: string
  type_project: string[]
  message_type: LabelValueObject[]
  selection_metrics: LabelValueObject[]
  tracking_metrics: TrackingMetricObject[]
  testing_methodology: TestingMethodology[]
}

export const getCepIntegrationTemplates = async (
  accountId: string
): Promise<CepTemplate[]> => {
  return (
    await connectInternalApi.get(
      `v1/core/marge/accounts/${accountId}/ceptemplate`
    )
  ).data.response.data
}

/**
 * Utility function to create a CepIntegration object from PayloadCepIntegration
 * @param payload The integration payload data
 * @param accountId The account ID
 * @returns A CepIntegration object
 */
export const createCepIntegrationFromPayload = (
  payload: PayloadCepIntegration
): Omit<CepIntegration, '_id'> => {
  return {
    name: payload.name,
    cep_name: payload.cep_name,
    distribution_channel: payload.distributionChannel,
    type_project: 'experiment',
    message_type: payload.messageType || payload.distributionType,
    testing_methodology: payload.testingMethod,
    credentials: payload.integrationCredentials,
    created_at: new Date().toISOString(),
    tracking_metrics: payload.trackingMetrics.map((tracking) => tracking.value),
  }
}

export const createIntegration = async ({
  payload,
  accountId,
}: {
  payload: PayloadCepIntegration
  accountId: string
}) => {
  await connectInternalApi.post<CepIntegration>(
    `v1/core/marge/accounts/${accountId}/integrations`,
    createCepIntegrationFromPayload(payload)
  )
}

export const deleteCepIntegration = async ({
  accountId,
  integrationId,
}: {
  accountId: string
  integrationId: string
}) => {
  await connectInternalApi.delete<void>(
    `v1/core/marge/accounts/${accountId}/integrations/${integrationId}`
  )
}

export const updateCepIntegration = async ({
  accountId,
  integrationId,
  payload,
}: {
  accountId: string
  integrationId: string
  payload: PayloadCepIntegration
}) => {
  await connectInternalApi.put<CepIntegration>(
    `v1/core/marge/accounts/${accountId}/integrations/${integrationId}`,
    createCepIntegrationFromPayload(payload)
  )
}

export const getIntegrationByIntegrationId = async ({
  integrationId,
  accountId,
}: {
  integrationId: string
  accountId: string
}) => {
  const result = await connectInternalApi.get<CepIntegration>(
    `v1/core/marge/accounts/${accountId}/integrations/${integrationId}`
  )
  return result.data
}
